import set from 'lodash/set';
import get from 'lodash/get';
import {
  FilterParserName,
  FilterParserOptions,
  FilterDefinition,
  RangeFilter,
  ArrayFilter,
  ArrayContainsFilter,
  EqualityFilter,
  AnyEqualityFilter,
  LikeFilter,
  JsonContainsFilter,
  ClosureFilter,
  NullFilter,
  DateCustomFilter,
  FilterUsageOption,
  AndFilter,
  GraphQLFilter,
  ArrayOverlapsFilter,
  ClosureFilterParserOptions,
  JsonFilterParserOptions,
  JsonContainsKeyFilter,
  DateCustomFilterParserName,
} from '../../@types';

//
// Parser typings and type guards
//

export interface FormatParseOptions {
  parserOptions?:
    | FilterParserOptions
    | ClosureFilterParserOptions
    | JsonFilterParserOptions;
}

const isJsonFilterOptions = (
  options: FormatParseOptions['parserOptions'],
): options is JsonFilterParserOptions => {
  return (options as JsonFilterParserOptions)?.path !== undefined;
};

const isClosureFilterOptions = (
  options: FormatParseOptions['parserOptions'],
): options is ClosureFilterParserOptions => {
  return (options as ClosureFilterParserOptions).depth !== undefined;
};

//
// Parser
//

type ValueParser = (value: string) => string | number | boolean;

const getParser = (
  valueType: FilterParserOptions['valueType'],
): ValueParser => {
  if (valueType === 'number') {
    return (value: string) => Number(value);
  }
  if (valueType === 'boolean') {
    return (value: string) => value.toLowerCase() === 'true';
  }
  return (value: string) => `${value}`;
};

/**
 * Mapper to string (used in parsers)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toString = (value: any) => `${value}`;

//
// Search
//

export const SEARCH_FILTER_PREFIX = 'search_';

/**
 * Formats the search filter to ID search_FILTERID
 *
 * @param id
 * @returns
 */
export const formatSearchFilterId = (id: string): string =>
  `${SEARCH_FILTER_PREFIX}${id}`;

/**
 * Checks whether this key is a search filter
 */
export const isSearchFilter = (id: string): boolean =>
  id.startsWith(SEARCH_FILTER_PREFIX);

//
// Filter Formatters
//

const formatRangeData = <T>(
  value: string,
  valueParser: (value: string) => T,
): RangeFilter<T> | NullFilter | undefined => {
  const [start, end] = value.split(',');
  if (start === 'null' && end === undefined) {
    return { isNull: true };
  }
  if (!start || !end) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse number-range data');
    return undefined;
  }

  const filterOutput: RangeFilter<T> = {};
  // Set lower bound
  if (start.length > 1) {
    if (start.startsWith('[')) {
      filterOutput.greaterThanOrEqualTo = valueParser(start.replace('[', ''));
    } else {
      filterOutput.greaterThan = valueParser(start.replace('(', ''));
    }
  }

  // Set higher bound
  if (end.length > 1) {
    if (end.endsWith(']')) {
      filterOutput.lessThanOrEqualTo = valueParser(end.replace(']', ''));
    } else {
      filterOutput.lessThan = valueParser(end.replace(')', ''));
    }
  }

  return filterOutput;
};

const formatArrayData = (
  value: Array<string>,
  include: boolean,
  parser: ValueParser,
) => {
  const hasNull = value.some((v) => v === 'null');
  const nonNullValues = value.filter((v) => v !== 'null');
  const result: Array<ArrayFilter & NullFilter> = [];
  if (nonNullValues.length > 0) {
    result.push({
      [include ? 'in' : 'notIn']: nonNullValues.map((v) => parser(v)),
    });
  }
  if (hasNull) {
    result.push({ isNull: include });
  }
  return !hasNull || nonNullValues.length === 0 ? result[0] : result;
};

const formatArrayContainsData = (
  value: Array<string>,
  parser: ValueParser,
) => ({
  contains: value.map((v) => parser(v) as string | number),
});

const formatEqualityData = (
  value: string | string[],
  equality: boolean,
  parser: ValueParser,
) => ({
  [equality ? 'equalTo' : 'notEqualTo']: Array.isArray(value)
    ? value.map((v) => parser(v))
    : parser(value),
});

const formatAnyEqualityData = (
  value: string | string[],
  equality: boolean,
  parser: ValueParser,
) => ({
  [equality ? 'anyEqualTo' : 'anyNotEqualTo']: Array.isArray(value)
    ? value.map((v) => parser(v))
    : parser(value),
});

const formatLikeData = (value: string, like: boolean) => ({
  [like ? 'likeInsensitive' : 'notLikeInsensitive']: `%${value}%`,
});

const formatArrayOverlaps = (value: string[], parser: ValueParser) => ({
  overlaps: value.map((v) => parser(v) as string | number),
});

const formatJsonContains = (value: string, opt: JsonFilterParserOptions) => ({
  contains: set<Record<string, unknown>>({}, opt.path, value),
});

const formatJsonContainsKey = (value: string) => ({
  containsKey: value,
});

const formatClosure = (
  value: string | FilterDefinition,
  opt: ClosureFilterParserOptions,
): FilterDefinition => ({
  some: {
    [opt.key ?? 'parentId']: value,
    depth: opt.depth ?? { equalTo: 1 },
  },
});

const formatCustomDateFilterData = (
  parser: DateCustomFilterParserName,
  value: string | string[],
): DateCustomFilter => {
  // Accept both a single value (radio) and an array (checkbox), ignoring the
  // blank "Todos" option, so date-part filters work with either widget type.
  const values = (Array.isArray(value) ? value : [value])
    .filter((v) => v !== '' && v !== null && v !== undefined)
    .map((v) => parseInt(v, 10));
  const result = {
    values,
    clientTimezone: 'America/Sao_Paulo',
  };

  let key = 'years';
  if (parser === 'in-months') key = 'months';
  if (parser === 'in-days-of-week') {
    key = 'daysOfWeek';
  }
  return { [key]: result };
};

/** List of parsers which result in array values */
const parsersWithArrayValues = [
  'in-array',
  'not-in-array',
  'contains',
  'closure-in-array',
  'closure-not-in-array',
  'array-overlaps',
  'in-years',
  'in-months',
  'in-days-of-week',
];

/**
 * Formats a filter data accordingly to its type
 *
 * TODO: We need to improve this function, the inner casts allow
 *       invalid values to be used if configuration is incorrect
 * @param {string} type the type of the parser to apply
 * @param {string} value the string value
 * @returns {FilterDefinition} the return value
 */
export const formatFilterData = (
  parser: FilterParserName,
  value: string | string[],
  options?: FormatParseOptions,
): FilterDefinition | FilterDefinition[] | undefined => {
  if (Array.isArray(value) && !parsersWithArrayValues.includes(parser)) {
    return value
      .map((v) => formatFilterData(parser, v, options))
      .filter((v) => !!v) as FilterDefinition[];
  }

  // Return undefined if value is empty
  if (!value) return undefined;

  // Get value type from options (default to string)
  const valueParser = getParser(options?.parserOptions?.valueType ?? 'string');

  if (parser === 'number-range') {
    return formatRangeData<number>(value as string, (v) => parseInt(v, 10));
  }
  if (parser === 'date-range' || parser === 'string-range') {
    return formatRangeData<string>(value as string, (v) => v);
  }
  if (parser === 'in-array' || parser === 'not-in-array') {
    return formatArrayData(
      value as string[],
      parser === 'in-array',
      valueParser,
    );
  }
  if (parser === 'contains') {
    return formatArrayContainsData(value as string[], valueParser);
  }
  if (parser === 'equality' || parser === 'inequality') {
    return formatEqualityData(value, parser === 'equality', valueParser);
  }
  if (parser === 'any-equal' || parser === 'any-not-equal') {
    return formatAnyEqualityData(value, parser === 'any-equal', valueParser);
  }
  if (parser === 'like' || parser === 'not-like') {
    return formatLikeData(value as string, parser === 'like');
  }
  if (
    parser === 'json-contains' &&
    options &&
    isJsonFilterOptions(options.parserOptions)
  ) {
    return formatJsonContains(value as string, options.parserOptions);
  }

  if (parser === 'json-contains-key') {
    return formatJsonContainsKey(value as string);
  }

  if (parser === 'array-overlaps') {
    return formatArrayOverlaps(value as string[], valueParser);
  }
  if (parser === 'uuid-search') {
    return formatEqualityData(value, true, valueParser);
  }
  if (
    parser.includes('closure-') &&
    options &&
    isClosureFilterOptions(options.parserOptions)
  ) {
    return formatClosure(
      (formatFilterData(
        parser.replace('closure-', '') as FilterParserName,
        value,
        options,
      ) as FilterDefinition) || (value as string),
      options.parserOptions,
    );
  }
  if (
    parser === 'in-years' ||
    parser === 'in-months' ||
    parser === 'in-days-of-week'
  ) {
    return formatCustomDateFilterData(parser, value as string[]);
  }
  return undefined;
};

//
// Filter Parsers
//

const parseRangeData = <T>(value: RangeFilter<T> | NullFilter) => {
  const result = [];

  // Check if this is a null filter data
  if ((value as NullFilter).isNull !== undefined) {
    return 'null';
  }

  const rangeValue = value as RangeFilter<T>;

  // Set lower bound
  if (rangeValue.greaterThan !== undefined) {
    result.push(`(${rangeValue.greaterThan}`);
  } else if (rangeValue.greaterThanOrEqualTo !== undefined) {
    result.push(`[${rangeValue.greaterThanOrEqualTo}`);
  } else {
    result.push('(');
  }

  // Set higher bound
  if (rangeValue.lessThan !== undefined) {
    result.push(`${rangeValue.lessThan})`);
  } else if (rangeValue.lessThanOrEqualTo !== undefined) {
    result.push(`${rangeValue.lessThanOrEqualTo}]`);
  } else {
    result.push(')');
  }
  return result.join(',');
};

const parseArrayData = (
  value: ArrayFilter | NullFilter | Array<ArrayFilter | NullFilter>,
  include: boolean,
) => {
  if (Array.isArray(value)) {
    const key = include ? 'in' : 'notIn';
    const arrayFilter = value.find((v) => key in v) as ArrayFilter | null;
    return [...(arrayFilter?.[key]?.map(toString) || []), 'null'];
  }
  if ('isNull' in value) return ['null'];
  return include
    ? (value as ArrayFilter).in?.map(toString)
    : (value as ArrayFilter).notIn?.map(toString);
};

const parseArrayContainsData = (value: ArrayContainsFilter) =>
  value.contains?.map(toString);

const parseEqualityData = (value: EqualityFilter, equality: boolean) => {
  const result = equality ? value.equalTo : value.notEqualTo;
  return Array.isArray(result) ? result.map(toString) : `${result}`;
};

const parseAnyEqualityData = (value: AnyEqualityFilter, equality: boolean) => {
  const result = equality ? value.anyEqualTo : value.anyNotEqualTo;
  return Array.isArray(result) ? result.map(toString) : `${result}`;
};

const parseLikeData = (value: LikeFilter, like: boolean) =>
  like
    ? value.likeInsensitive?.slice(1, -1)
    : value.notLikeInsensitive?.slice(1, -1);

const parseArrayOverlaps = (value: ArrayOverlapsFilter) =>
  value.overlaps?.map(toString);

const parseJsonContainsData = (
  value: JsonContainsFilter,
  opt: JsonFilterParserOptions,
) => get(value.contains, opt.path) as string;

const parseJsonContainsKeyData = (value: JsonContainsKeyFilter): string =>
  value.containsKey as string;

const parseClosure = <T>(
  value: ClosureFilter<T>,
  opt: ClosureFilterParserOptions,
) => get(value.some, opt.key ?? 'parentId');

const parseCustomDateFilterData = (
  parser: DateCustomFilterParserName,
  value: DateCustomFilter,
) => {
  if (parser === 'in-months') {
    return value.months?.values.map((v) => `${v}`) || [];
  }
  if (parser === 'in-days-of-week') {
    return value.daysOfWeek?.values.map((v) => `${v}`) || [];
  }
  return value.years?.values.map((v) => `${v}`) || [];
};

/**
 * Parses a filter data accordingly to its type
 *
 * TODO: We need to improve this functions, the inner casts allow
 *       invalid values to be used if configuration is incorrect
 * @param {string} type the type of the parser to apply
 * @param {string} value the string value
 * @returns {string} the return value
 */
export const parseFilterData = (
  parser: FilterParserName,
  value: FilterDefinition | FilterDefinition[],
  options?: FormatParseOptions,
): string | string[] | undefined => {
  if (Array.isArray(value) && !parsersWithArrayValues.includes(parser)) {
    return value
      .map((v: FilterDefinition) => parseFilterData(parser, v, options))
      .filter((v) => !!v) as string[];
  }

  if (parser === 'number-range') {
    return parseRangeData(value as RangeFilter<number>);
  }
  if (parser === 'date-range' || parser === 'string-range') {
    return parseRangeData(value as RangeFilter<string>);
  }
  if (parser === 'in-array' || parser === 'not-in-array') {
    return parseArrayData(value as ArrayFilter, parser === 'in-array');
  }
  if (parser === 'contains') {
    return parseArrayContainsData(value as ArrayContainsFilter);
  }
  if (parser === 'equality' || parser === 'inequality') {
    return parseEqualityData(value as EqualityFilter, parser === 'equality');
  }
  if (parser === 'any-equal' || parser === 'any-not-equal') {
    return parseAnyEqualityData(
      value as AnyEqualityFilter,
      parser === 'any-equal',
    );
  }
  if (parser === 'like' || parser === 'not-like') {
    return parseLikeData(value as LikeFilter, parser === 'like');
  }
  if (
    parser === 'json-contains' &&
    options &&
    isJsonFilterOptions(options.parserOptions)
  ) {
    return parseJsonContainsData(
      value as JsonContainsFilter,
      options.parserOptions,
    );
  }

  if (parser === 'json-contains-key') {
    return parseJsonContainsKeyData(value as JsonContainsKeyFilter);
  }

  if (parser === 'array-overlaps') {
    return parseArrayOverlaps(value as ArrayOverlapsFilter);
  }
  if (parser === 'uuid-search') {
    return parseEqualityData(value as EqualityFilter, true);
  }
  if (
    parser.includes('closure-') &&
    options &&
    isClosureFilterOptions(options.parserOptions)
  ) {
    return parseFilterData(
      parser.replace('closure-', '') as FilterParserName,
      parseClosure(
        value as ClosureFilter<EqualityFilter | ArrayFilter>,
        options.parserOptions,
      ) || {},
      options,
    );
  }
  if (
    parser === 'in-years' ||
    parser === 'in-months' ||
    parser === 'in-days-of-week'
  ) {
    return parseCustomDateFilterData(parser, value as DateCustomFilter);
  }
  return `${value}`;
};

//
// Filter Modifier
//

/**
 * Applies filter modifiers defined by FilterUsageOption['modifier'] type.
 * @param key the original filter key
 * @param value the original filter value
 * @param modifier the modifier configuration
 * @returns an array in the format [key, value] with the modified key/value
 */
export const applyModifier = (
  key: string,
  value: FilterDefinition,
  modifier?: FilterUsageOption['modifier'],
): [string, FilterDefinition] => {
  if (!modifier) {
    const parts = key.split(/\.([\s\S]*)/, 2);
    if (parts.length > 1) {
      return [parts[0], set({}, parts[1], value)];
    }
    return [key, value];
  }

  let altKey = key;
  let altValue = value;

  // Modify filter parser if defined
  // this should be performed before changing key settings
  if (modifier.parser) {
    const { parser } = modifier;
    altValue = formatFilterData(
      parser.to,
      parseFilterData(parser.from, value, {
        parserOptions: parser.fromOptions,
      }) || '',
      { parserOptions: parser.toOptions },
    ) as FilterDefinition;
  }

  // Map filter to another key
  if (modifier.key) {
    const parts = modifier.key.split(/\.([\s\S]*)/, 2);
    if (parts.length > 1) {
      altValue = set({}, parts[1], altValue);
      [altKey] = parts;
    } else {
      altKey = modifier.key;
    }
  }

  return [altKey, altValue];
};

//
// Permission filter
//

/**
 * Merges a permission filter into an already parsed filter.
 *
 * A permission key may name a filter the user can also pick — e.g. `unitId` is
 * restricted by the account permissions *and* offered as a page filter. Writing
 * both to the same key would either drop the restriction (a spread keeps the
 * last one) or splice both `in` arrays together entry by entry (a lodash deep
 * merge), so a key already present in the parsed filter is appended to `and`
 * instead of being merged.
 *
 * @param filter the filter parsed from the base, custom and user filters
 * @param permissionFilter the filter derived from the account permissions
 * @returns the parsed filter with the permission restrictions applied
 */
export const mergePermissionFilter = (
  filter: GraphQLFilter,
  permissionFilter?: GraphQLFilter,
): GraphQLFilter => {
  const entries = Object.entries(permissionFilter || {});
  const conflicting = entries.filter(([key]) => filter[key] !== undefined);

  const result: GraphQLFilter = {
    ...filter,
    ...Object.fromEntries(entries.filter(([key]) => filter[key] === undefined)),
  };

  if (conflicting.length === 0) return result;

  return {
    ...result,
    and: [
      ...(Array.isArray(result.and) ? result.and : []),
      ...conflicting.map(([key, value]) => ({ [key]: value })),
    ] as AndFilter<GraphQLFilter>['and'],
  };
};
